import React, { useState, useEffect } from 'react';
import { MODULES, ACTIONS, usePermissions } from '../context/PermissionsContext';
import api from '../services/api';

const UserPermissionsManager = () => {
  const { refreshPermissions, currentUser } = usePermissions();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersData = await api.users.getAll();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      setLoading(true);
      const response = await api.userPermissions.getByUserId(userId);
      if (response && response.permissions) {
        setUserPermissions(response.permissions);
      } else {
        setUserPermissions({});
      }
    } catch (error) {
      console.log('No permissions found for user');
      setUserPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    loadUserPermissions(user.id);
  };

  const handlePermissionToggle = (module, action) => {
    setUserPermissions(prev => {
      const modulePerms = prev[module] || [];
      const hasPermission = modulePerms.includes(action);
      
      return {
        ...prev,
        [module]: hasPermission 
          ? modulePerms.filter(a => a !== action)
          : [...modulePerms, action]
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await api.userPermissions.update(selectedUser.id, { permissions: userPermissions });
      
      // Refresh permissions if updating current user's permissions
      if (currentUser && currentUser.id === selectedUser.id) {
        await refreshPermissions();
      }
      
      setMessage({ text: 'Permissions updated successfully! Changes applied immediately.', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: 'Error updating permissions', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">User Permissions Management</h5>
      </div>
      <div className="card-body ">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
            {message.text}
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label">Select User</label>
            <select 
              className="form-control"
              onChange={(e) => {
                const user = users.find(u => u.id === parseInt(e.target.value));
                if (user) handleUserSelect(user);
              }}
              value={selectedUser?.id || ''}
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} ({user.role || 'No Role'})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-8 d-flex align-items-end">
            <button 
              className="btn btn-primary"
              onClick={handleSavePermissions}
              disabled={!selectedUser || loading}
            >
              {loading ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>

        {selectedUser && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Module</th>
                  <th>List</th>
                  <th>View</th>
                  <th>Create</th>
                  <th>Edit</th>
                  <th>Delete</th>
                  <th>Approve</th>
                  <th>Export</th>
                  <th>Reject</th>
                  <th>Info</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(MODULES).map(module => (
                  <tr key={module}>
                    <td className="fw-bold">
                      {module.replace('_', ' ').toUpperCase()}
                    </td>
                    {Object.values(ACTIONS).map(action => (
                      <td key={action} className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={userPermissions[module]?.includes(action) || false}
                          onChange={() => handlePermissionToggle(module, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!selectedUser && (
          <div className="text-center text-muted py-4">
            <p>Select a user to manage their permissions</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPermissionsManager;